import type { Game, Member, Room, Season } from '../../types/game'

export interface MobileLogEntry {
  kind: 'game' | 'chat'
  timestamp: Date
  season?: Season
  sender?: string
  color?: string | null
  text: string
  rawText?: string
  aliasByColor?: Record<string, string>
}

export function buildMobileLogEntries(room: Room, game: Game | null, memberId: string): MobileLogEntry[] {
  const selfMember = room.members.find((member) => member.id === memberId) ?? null
  const selfColor = selfMember?.color ?? null
  const allMembers = [...room.left_members, ...room.members]
  const aliasByColor = Object.fromEntries(
    allMembers
      .filter((member) => member.color !== null)
      .map((member) => [member.color as string, member.alias])
  )
  const memberById = new Map<string, Member>(allMembers.map((member) => [member.id, member]))
  const entries: MobileLogEntry[] = []

  if (game) {
    for (const log of game.logs) {
      if (log.target !== null && log.target !== selfColor) continue
      entries.push({
        kind: 'game',
        timestamp: new Date(log.timestamp),
        season: log.season,
        text: log.message.replace(/\{([^}]+)\}/g, (_, color) => aliasByColor[color] ?? color),
        rawText: log.message,
        aliasByColor,
      })
    }
  }

  for (const record of room.buffer ?? []) {
    if (record.type !== 'chat') continue

    const target = typeof record.payload.target === 'string' ? record.payload.target : undefined
    const userId = typeof record.payload.user === 'string' ? record.payload.user : undefined
    if (!userId) continue
    if (target && target !== memberId && userId !== memberId) continue

    const member = memberById.get(userId)
    entries.push({
      kind: 'chat',
      timestamp: new Date(record.timestamp),
      sender: member?.alias ?? 'Unknown',
      color: member?.color ?? null,
      text: String(record.payload.text ?? ''),
    })
  }

  return entries.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}
